import { Injectable } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApplicationHttpClient } from '../components/shared/http/application-http-client';
import { Routes } from '../config/routes';
import { environment } from '../../environments/environment';

interface IntercomIdentityResponse {
    intercomUserJwt: string | null;
    expiresAt: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class IntercomIdentityService {
    private cachedJwt: string | null = null;
    private expiresAt: Date | null = null;

    constructor(private _http: ApplicationHttpClient) {}

    /**
     * Get Intercom identity JWT, fetching from API if needed.
     * Uses caching to avoid unnecessary API calls.
     * Returns null immediately if Intercom is disabled in environment.
     */
    getIdentityJwt(): Observable<string | null> {
        // Skip API call if Intercom is disabled
        if (environment.Intercom.disabled) {
            return observableOf(null);
        }

        // Return cached JWT if still valid (with 5-minute buffer)
        if (this.cachedJwt && this.expiresAt) {
            const bufferTime = 5 * 60 * 1000; // 5 minutes
            if (new Date().getTime() < this.expiresAt.getTime() - bufferTime) {
                return observableOf(this.cachedJwt);
            }
        }

        // Fetch new JWT from API
        return this._http.get<IntercomIdentityResponse>(
            Routes.MakeLegFiCoreUrl('/intercom/identity')
        ).pipe(
            tap(response => {
                // Only cache if we got a valid JWT
                if (response.intercomUserJwt && response.expiresAt) {
                    this.cachedJwt = response.intercomUserJwt;
                    this.expiresAt = new Date(response.expiresAt);
                }
            }),
            map(response => response.intercomUserJwt),
            catchError(error => {
                console.error('Failed to fetch Intercom identity JWT:', error);
                return observableOf(null);
            })
        );
    }

    /**
     * Clear cached JWT (call on logout).
     */
    clearCache(): void {
        this.cachedJwt = null;
        this.expiresAt = null;
    }
}
